'use client'

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { OtherIncomeListItem } from '@garage/shared'
import { OTHER_INCOME_KIND_LABELS } from '@garage/shared'
import {
  Button,
  IconButton,
  DataTable,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  type Column,
  EditIcon,
  DeleteIcon,
  AddIcon,
  ChevronRightIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters } from '@/lib/hooks.js'
import { OtherIncomeFormDialog } from '@/components/OtherIncomeFormDialog.js'

/** First and last day ("YYYY-MM-DD") of a given month, 0-indexed like Date. */
function monthRange(year: number, month: number): { from: string; to: string } {
  const lastDay = new Date(year, month + 1, 0).getDate()
  return {
    from: `${year}-${String(month + 1).padStart(2, '0')}-01`,
    to: `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
  }
}

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const

export function OtherIncomeTab() {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const formatters = useFormatters()
  const { t } = useTranslation()

  const today = new Date()
  const [listYear, setListYear] = useState(today.getFullYear())
  const [listMonth, setListMonth] = useState(today.getMonth())

  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [editingIncome, setEditingIncome] = useState<OtherIncomeListItem | undefined>()
  const [formDialogOpen, setFormDialogOpen] = useState(false)

  const monthName = (month: number) => t(`calendar.months.${MONTH_KEYS[month]}`)
  const listRange = monthRange(listYear, listMonth)

  // Monthly other income
  const { data: monthlyData, isLoading } = useQuery({
    queryKey: ['other-income', { ...listRange, pageSize: 200 }],
    queryFn: () =>
      api.otherIncome.list({
        from: listRange.from,
        to: listRange.to,
        pageSize: 200,
      }),
  })

  const goToMonth = (delta: number) => {
    const next = new Date(listYear, listMonth + delta, 1)
    setListYear(next.getFullYear())
    setListMonth(next.getMonth())
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.otherIncome.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.otherIncome({}) })
      queryClient.invalidateQueries({ queryKey: ['finance-advanced'] })
      snackbar.success(t('accounting.advanced.incomeRemoved'))
      setDeleteId(null)
    },
    onError: (error) => {
      snackbar.error(error instanceof ApiError ? error.message : t('common.errorOccurred'))
    },
  })

  const handleEdit = (income: OtherIncomeListItem) => {
    setEditingIncome(income)
    setFormDialogOpen(true)
  }

  const handleFormClose = () => {
    setFormDialogOpen(false)
    setEditingIncome(undefined)
  }

  const handleAddNew = () => {
    setEditingIncome(undefined)
    setFormDialogOpen(true)
  }

  const items = monthlyData?.items ?? []
  const isEmpty = items.length === 0

  // Calculate month total
  const monthTotal = items.reduce((sum, item) => sum + item.amountBeforeVatMinor + item.vatMinor, 0)

  const columns: Column<OtherIncomeListItem>[] = [
    {
      key: 'date',
      header: t('calendar.date'),
      cell: (row) => <div>{formatters.date(row.date)}</div>,
    },
    {
      key: 'kind',
      header: t('accounting.advanced.kind'),
      cell: (row) => {
        const label = t(`accounting.advanced.incomeKinds.${row.kind}`, OTHER_INCOME_KIND_LABELS[row.kind])
        return <div>{label}</div>
      },
    },
    {
      key: 'description',
      header: t('accounting.advanced.description'),
      cell: (row) => <div>{row.description}</div>,
    },
    {
      key: 'amountBeforeVatMinor',
      header: t('accounting.beforeVat'),
      cell: (row) => <div className="tabular">{formatters.amount(row.amountBeforeVatMinor)}</div>,
      numeric: true,
    },
    {
      key: 'vatMinor',
      header: t('accounting.vat'),
      cell: (row) => <div className="tabular">{formatters.amount(row.vatMinor)}</div>,
      numeric: true,
    },
    {
      key: 'total',
      header: t('dashboard.columns.total'),
      cell: (row) => (
        <div className="tabular font-medium">
          {formatters.amount(row.amountBeforeVatMinor + row.vatMinor)}
        </div>
      ),
      numeric: true,
    },
    {
      key: 'paymentMethod',
      header: t('accounting.advanced.paymentMethod'),
      cell: (row) => {
        const labels: Record<string, string> = {
          cash: t('accounting.advanced.paymentMethods.cash'),
          card: t('accounting.advanced.paymentMethods.card'),
          bank: t('accounting.advanced.paymentMethods.bank'),
          other: t('accounting.advanced.paymentMethods.other'),
        }
        return <div className="text-body-sm text-on-surface-variant">{labels[row.paymentMethod]}</div>
      },
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex gap-2 justify-end">
          <IconButton
            icon={<EditIcon className="w-5 h-5" />}
            label={t('common.edit')}
            variant="standard"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(row)
            }}
          />
          <IconButton
            icon={<DeleteIcon className="w-5 h-5" />}
            label={t('common.delete')}
            variant="danger"
            onClick={(e) => {
              e.stopPropagation()
              setDeleteId(row.id)
            }}
          />
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <Button variant="filled" icon={<AddIcon className="w-5 h-5" />} onClick={handleAddNew}>
          {t('accounting.advanced.addIncome')}
        </Button>
      </div>

      {/* Monthly income section */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconButton
              icon={<ChevronRightIcon className="w-5 h-5 rotate-180 rtl:rotate-0" />}
              label={t('calendar.previousMonth')}
              variant="standard"
              onClick={() => goToMonth(-1)}
            />
            <h2 className="text-title-md text-on-surface w-40 text-center">
              {monthName(listMonth)} {listYear}
            </h2>
            <IconButton
              icon={<ChevronRightIcon className="w-5 h-5 rtl:rotate-180" />}
              label={t('calendar.nextMonth')}
              variant="standard"
              onClick={() => goToMonth(1)}
            />
          </div>
          <Button
            variant="text"
            onClick={() => { setListYear(today.getFullYear()); setListMonth(today.getMonth()) }}
          >
            {t('calendar.today')}
          </Button>
        </div>

        {isLoading ? (
          <Skeleton lines={3} />
        ) : isEmpty ? (
          <EmptyState
            title={t('accounting.advanced.noIncome', { month: monthName(listMonth), year: listYear })}
            description={t('accounting.advanced.noIncomeDesc')}
            action={<Button variant="tonal" onClick={handleAddNew}>{t('accounting.advanced.addIncome')}</Button>}
          />
        ) : (
          <>
            <div className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
              <DataTable
                columns={columns}
                rows={items}
                rowKey={(c) => c.id}
                stackOnMobile
              />
            </div>
            {/* Month total line */}
            <div className="flex items-center justify-between px-5 py-3 bg-surface-low border border-outline-variant rounded-lg">
              <span className="text-label-lg text-on-surface">{t('accounting.advanced.monthTotal')}</span>
              <div className="text-title-md text-on-surface font-medium tabular">{formatters.money(monthTotal)}</div>
            </div>
          </>
        )}
      </section>

      <OtherIncomeFormDialog open={formDialogOpen} income={editingIncome} onClose={handleFormClose} />

      <ConfirmDialog
        open={!!deleteId}
        title={t('accounting.advanced.removeIncomeTitle')}
        message={t('accounting.advanced.removeIncomeMessage')}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteId) deleteMutation.mutate(deleteId)
        }}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  )
}
